/**
 * Pop-up "o médico colou um recado no mural" na tela da RECEPÇÃO.
 *
 * Quando o médico transforma uma mensagem do chat em post-it
 * ([[ChatToMuralModal]]), o post-it nasce com `receptionAlert` e pipoca aqui,
 * em qualquer página — componente global montado no Shell, ao lado do
 * [[CallNextWatcher]].
 *
 * Regras:
 *  - Só aparece para papéis de recepção (ADMIN / SECRETARIA / RECEPCIONISTA),
 *    mesma regra do [[CallNextWatcher]]. Quem colou não vê o próprio aviso.
 *  - Quem decide o que pipoca é `pendingReceptionAlerts` (só os de hoje, sem
 *    "Ciente", dentro do escopo de agenda do perfil).
 *  - O "Ciente" fica gravado NO POST-IT: o primeiro da recepção que confirma
 *    tira o pop-up de todas as telas, e o médico vê no mural que leram.
 *  - Não tem RTDB nem assinatura própria: é a MESMA consulta do badge da
 *    Sidebar (recados em aberto), que o Firestore compartilha — custo zero.
 *  - Sem véu no fundo: o aviso não exige ação imediata como "chamar
 *    paciente"; a recepção continua vendo e usando a tela de trás.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Clock, MessageCircle, StickyNote, User as UserIcon } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { canSee } from '@/hooks/usePendencias';
import { useMuralOpen } from '@/hooks/useMuralNotes';
import { MuralNoteService, pendingReceptionAlerts } from '@/services/firebase/muralNotes';
import { CATEGORY_META } from '@/pages/mural/muralUi';
import { playCallChime } from '@/utils/chime';
import type { MuralNote } from '@/types/models';

function fmtTime(ms: number | undefined): string {
  if (!ms) return '';
  return new Date(ms).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
}

export const MuralAlertWatcher = () => {
  const { clinicId, role, user, profile } = useAuth();
  const navigate = useNavigate();
  const uid = user?.uid;
  const enabled = canSee(role, 'BRIEFING_NEEDED');
  const { notes } = useMuralOpen(enabled ? clinicId : null, role);
  // Chave estável pro memo (o array muda de identidade a cada render).
  const scopeKey = (profile?.allowedProfessionalIds ?? []).join(',');
  // Some na hora do clique, sem esperar o snapshot voltar com o "Ciente".
  const [dismissed, setDismissed] = useState<string[]>([]);

  const pending = useMemo(() => {
    if (!enabled) return [];
    const scope = scopeKey ? scopeKey.split(',') : [];
    return pendingReceptionAlerts(notes, { uid, scope }).filter((n) => !dismissed.includes(n.id));
  }, [enabled, notes, uid, scopeKey, dismissed]);

  // Toca o ding uma vez por post-it novo na fila (não a cada snapshot).
  const chimedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const fresh = pending.filter((n) => !chimedRef.current.has(n.id));
    if (fresh.length === 0) return;
    fresh.forEach((n) => chimedRef.current.add(n.id));
    playCallChime();
  }, [pending]);

  if (pending.length === 0 || !clinicId) return null;

  const current = pending[0];
  const ack = (note: MuralNote) => {
    setDismissed((d) => [...d, note.id]);
    MuralNoteService.ackReceptionAlert(clinicId, note.id, {
      uid: uid ?? '',
      name: profile?.displayName ?? undefined,
    }).catch((err) => console.error('[mural-alert] ciente:', err));
  };

  return (
    <MuralAlertPopup
      note={current}
      remaining={pending.length - 1}
      onAck={() => ack(current)}
      onOpenMural={() => {
        ack(current);
        navigate('/mural');
      }}
    />
  );
};

/** O cartão do aviso, sem Firestore — o harness /dev/mural?alerta=1 usa direto. */
export const MuralAlertPopup = ({
  note,
  remaining,
  onAck,
  onOpenMural,
}: {
  note: MuralNote;
  remaining: number;
  onAck: () => void;
  onOpenMural: () => void;
}) => {
  const meta = CATEGORY_META[note.category] ?? CATEGORY_META.outro;
  return (
    <div className="pointer-events-none fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div
        role="alertdialog"
        aria-label="Novo recado no mural"
        className="pointer-events-auto w-full max-w-md overflow-hidden rounded-2xl border border-outline-variant/40 bg-surface shadow-float animate-fade-in"
      >
        <div className="flex items-center gap-2 bg-primary px-5 py-3 text-on-primary">
          <StickyNote size={22} className="shrink-0" />
          <span className="font-headline text-lg font-bold">Recado no mural</span>
        </div>

        <div className="space-y-4 p-6">
          <p className="text-on-surface">
            <strong>{note.createdByName || 'O médico'}</strong> colou uma mensagem do chat no mural:
          </p>

          <div className={`rounded-lg border px-3 py-2.5 ${meta.paper}`}>
            <p className={`font-mono text-[10px] font-bold uppercase tracking-wider ${meta.ink}`}>{meta.label}</p>
            {note.patientName && (
              <p className="mt-1 flex items-center gap-1.5 text-sm font-bold text-on-surface">
                <UserIcon size={14} className="shrink-0" />
                <span className="truncate">{note.patientName}</span>
              </p>
            )}
            <p className="mt-1 line-clamp-6 whitespace-pre-wrap break-words text-[15px] leading-snug text-on-surface">
              {note.text}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-on-surface-variant">
            {note.fromChat?.senderName && (
              <span className="flex items-center gap-1">
                <MessageCircle size={12} /> do chat · {note.fromChat.senderName}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock size={12} /> colado às {fmtTime(note.createdAt)}
            </span>
          </div>

          {remaining > 0 && (
            <p className="text-xs font-bold text-primary">
              + {remaining} outro{remaining > 1 ? 's' : ''} recado{remaining > 1 ? 's' : ''} na fila
            </p>
          )}

          <div className="flex gap-2">
            <button
              onClick={onOpenMural}
              className="flex-1 rounded-xl border border-outline-variant py-3 text-sm font-bold text-on-surface transition-colors hover:bg-surface-container-high"
            >
              Abrir mural
            </button>
            <button
              onClick={onAck}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-3 text-base font-bold text-on-primary transition-all hover:brightness-110"
            >
              <Check size={18} /> Ciente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
